/**
 * E2E-only record of the native-preset requests the UI actually SUBMITS.
 *
 * A hardware test that reads only receipts is reasoning backwards: it sees what
 * the encoder published and infers what must have been asked for. That cannot
 * distinguish "this click produced this operation" from "some operation
 * completed around the time of this click", and it cannot learn the action code
 * of a request without first trusting a receipt to tell it. The regression this
 * guards — a recall routed at a native slot that was never there — is precisely
 * a case where the request and the receipt disagree.
 *
 * So the submitted request is recorded at the one place it exists in full:
 * `ProxyImpl.mutate`, immediately before the mutation is dispatched. This is
 * observation only. It copies scalars out of a command that is already built,
 * changes no dispatch, no return value and no state, and is installed only
 * under the same IRIS_E2E_TEST gate the other renderer probes use.
 *
 * Per rule reference-rules-in-comments (ai.knowledge/global); sibling probes:
 * device-probe.ts, preset-action-telemetry-probe.ts.
 */

/** One submitted native-preset request, as the UI handed it to the agent. */
export type NativePresetSubmission = {
  /** Monotonic position in this session's submission log. */
  cursor: number;
  /** Wall clock, for ordering against test-side events only. */
  at: number;
  /** The agent the mutation was submitted to (`proxy.id`). */
  agentId: string;
  /** The device the command names. */
  deviceId: string;
  operationId: string;
  generation: number;
  action: number;
  /** The PRESET id — `studioId` in the command is the preset, not the room. */
  studioId: string;
};

/**
 * The submission log. Bounded: a run that overflows it reports `dropped` so a
 * truncated history fails attribution instead of reading as complete.
 */
const MAX_SUBMISSIONS = 2048;
const submissions: NativePresetSubmission[] = [];
let nextCursor = 0;
let dropped = 0;

/** Read accessors over the log, for the probe that exposes it. */
export const submissionLog = {
  cursor: (): number => nextCursor,
  since: (cursor: number): NativePresetSubmission[] => submissions.filter((s) => s.cursor > cursor),
  dropped: (): number => dropped
};

/** What the probe exposes to Playwright, and to the agent layer's hook. */
export type SubmissionProbe = {
  /**
   * Record one submitted command. Called by ProxyImpl.mutate through the
   * globalThis hook, so the agent layer needs no import of this file.
   */
  record(agentId: string, set: unknown): void;
  /** Current cursor. Arm before a click, then read what arrived after it. */
  cursor(): number;
  /** Submissions recorded strictly after `cursor`, oldest first. */
  since(cursor: number): NativePresetSubmission[];
  /** How many submissions were discarded because the log was full. */
  dropped(): number;
};

/**
 * Record one submitted native-preset command.
 *
 * Called from `ProxyImpl.mutate` before dispatch. Every field is copied by
 * value; nothing here retains a reference into the command, and nothing it does
 * can fail the mutation — a throw inside diagnostics must never break the
 * operator's action, so the whole body is guarded.
 */
export function recordNativePresetSubmission(agentId: string, set: unknown): void {
  try {
    const nativePreset = (
      set as
        | { command?: { device?: { nativePreset?: Record<string, unknown> } } }
        | null
        | undefined
    )?.command?.device?.nativePreset;
    if (!nativePreset) return;

    if (submissions.length >= MAX_SUBMISSIONS) {
      dropped += 1;
      return;
    }
    nextCursor += 1;
    submissions.push({
      cursor: nextCursor,
      at: Date.now(),
      agentId,
      deviceId: String(nativePreset.id ?? ''),
      operationId: String(nativePreset.operationId ?? ''),
      generation: Number(nativePreset.generation ?? 0),
      action: Number(nativePreset.action ?? 0),
      studioId: String(nativePreset.studioId ?? '')
    });
  } catch {
    // Diagnostics must not affect the operation being diagnosed.
  }
}
